import { tns } from 'tiny-slider/src/tiny-slider'
import GLightbox from 'glightbox'
import { trackPDPVideoSlideImpression } from './analytics'

function initListingCarousel () {
  const container = document.getElementById('vjs-listing-carousel')
  if (!container) return

  const slidesData = JSON.parse(container.dataset.slides || '[]')
  const thumbnailNav = document.getElementById('vjs-listing-carousel-thumbnails')

  const slider = initListingCarouselSlider()
  const lightbox = createLightbox(slidesData)

  setupSlideChangeHandler(slider, slidesData, thumbnailNav, container)
  setupLightboxClickHandler(container, slider, slidesData, lightbox)
  setupDragDetection(container)

  // First 4 slides are eager-loaded server-side, so we start preloading from slide 4+
  // Runs on init in case user jumps directly to a later slide via thumbnails
  eagerLoadAdjacentSlides(container, 0, slidesData.length, 4)
}

function initListingCarouselSlider () {
  return tns({
    container: '#vjs-listing-carousel',
    items: 1,
    slideBy: 1,
    controls: true,
    controlsPosition: 'bottom',
    controlsContainer: '#vjs-listing-carousel-controls',
    nav: true,
    navContainer: '#vjs-listing-carousel-thumbnails',
    navAsThumbnails: true,
    loop: true,
    speed: 500,
    lazyload: true,
    lazyloadSelector: '.vjs-lazy',
    preventScrollOnTouch: 'auto',
    swipeAngle: 30,
    touch: true,
    mouseDrag: true
  })
}

function createLightbox (slidesData) {
  const elements = buildLightboxElements(slidesData)
  return GLightbox({
    elements: elements,
    touchNavigation: true,
    loop: true,
    autoplayVideos: false,
    openEffect: 'fade',
    closeEffect: 'fade'
  })
}

function setupSlideChangeHandler (slider, slidesData, thumbnailNav, container) {
  slider.events.on('indexChanged', info => {
    const realIndex = getRealIndex(info.index, slidesData.length)

    handleVideoAutoplay(slidesData[realIndex])
    pauseOffScreenVideos(realIndex)
    scrollThumbnailIntoView(thumbnailNav, realIndex)
    eagerLoadAdjacentSlides(container, realIndex, slidesData.length, 2)
  })
}

// Auto-play video when sliding to a video slide
function handleVideoAutoplay (slideData) {
  if (!slideData || !slideData.is_video) return

  const video = document.getElementById('vjs-carousel-video-slide')
  if (video) {
    video.play()
    trackPDPVideoSlideImpression()
  }
}

function setupLightboxClickHandler (container, slider, slidesData, lightbox) {
  container.addEventListener('click', e => {
    const slideElement = e.target.closest('.vjs-listing-slide')
    if (!slideElement) return

    // Don't open lightbox for video slides
    if (slideElement.classList.contains('vjs-video-slide')) return
    if (e.target.tagName === 'VIDEO') return

    const info = slider.getInfo()
    const realIndex = getRealIndex(info.index, slidesData.length)
    const slideData = slidesData[realIndex]

    if (slideData && !slideData.is_video) {
      const lightboxIndex = getLightboxIndex(slidesData, realIndex)
      lightbox.openAt(lightboxIndex)
    }
  })
}

// Prevent drag gestures from triggering click events
function setupDragDetection (container) {
  const dragState = { isDragging: false, startX: 0 }
  const DRAG_THRESHOLD = 10

  container.addEventListener('mousedown', e => {
    dragState.isDragging = false
    dragState.startX = e.clientX
  })

  container.addEventListener('mousemove', e => {
    if (Math.abs(e.clientX - dragState.startX) > DRAG_THRESHOLD) {
      dragState.isDragging = true
    }
  })

  container.addEventListener('click', e => {
    if (dragState.isDragging) {
      e.preventDefault()
      e.stopPropagation()
    }
  }, true)
}

function getRealIndex (sliderIndex, totalSlides) {
  return ((sliderIndex - 1) % totalSlides + totalSlides) % totalSlides
}

/**
 * Eagerly load adjacent slides by swapping data-src to src
 * This ensures images are fully loaded before the user swipes to them
 * Also handles cloned slides (created by tiny-slider for infinite loop effect)
 */
function eagerLoadAdjacentSlides (container, currentIndex, totalSlides, count) {
  const offsets = [...Array(count).keys()].map(n => n + 1)

  const indicesToLoad = offsets.flatMap(offset => {
    const nextIndex = (currentIndex + offset) % totalSlides
    const prevIndex = (currentIndex - offset + totalSlides) % totalSlides
    return [nextIndex, prevIndex]
  })

  // Find and load each slide's image (including clones)
  indicesToLoad.forEach(index => {
    // Find ALL slides with this index (originals + clones created by tiny-slider)
    const slideElements = container.querySelectorAll('[data-index="' + index + '"]')
    slideElements.forEach(slideElement => {
      const img = slideElement.querySelector('img[data-src]')
      if (img) loadLazyImage(img)
    })
  })
}

function loadLazyImage (img) {
  const src = img.getAttribute('data-src')
  if (src) {
    // Also load all other images with the same data-src (clones)
    const allMatchingImages = document.querySelectorAll('img[data-src="' + src + '"]')
    allMatchingImages.forEach(matchingImg => {
      matchingImg.setAttribute('src', src)
      matchingImg.removeAttribute('data-src')
      matchingImg.classList.remove('vjs-lazy', 'vsd-lazy-placeholder')
    })
  }
}

function buildLightboxElements (slidesData) {
  return slidesData
    .filter(slide => !slide.is_video)
    .map(slide => ({
      href: slide.original,
      type: 'image',
      alt: slide.alt_text || ''
    }))
}

function getLightboxIndex (slidesData, slideIndex) {
  return slidesData.slice(0, slideIndex).filter(slide => !slide.is_video).length
}

function pauseOffScreenVideos (currentIndex) {
  const videos = document.querySelectorAll('#vjs-listing-carousel video')
  videos.forEach(video => {
    const slideElement = video.closest('.vjs-listing-slide')

    if (slideElement) {
      const slideIndex = parseInt(slideElement.dataset.index, 10)
      if (slideIndex !== currentIndex) {
        video.pause()
      }
    }
  })
}

function scrollThumbnailIntoView (thumbnailNav, index) {
  if (!thumbnailNav) return

  // Only scroll if thumbnail bar is visible in viewport
  const rect = thumbnailNav.getBoundingClientRect()
  const isVisible = rect.top < window.innerHeight && rect.bottom > 0
  if (!isVisible) return

  const thumbnails = thumbnailNav.querySelectorAll('.vjs-thumbnail')
  const activeThumbnail = thumbnails[index]

  if (activeThumbnail) {
    activeThumbnail.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'center'
    })
  }
}

export default initListingCarousel
export { initListingCarousel }
